import React from 'react';

import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { IDatasheetResultElement } from "@modules/datasheets/components/result/types/datasheet-element";
import { getCommonFieldBasedElementParams, useDatasheetElementController } from '@modules/datasheets/components/result/controllers/datasheet-element';
import { uploadDocument } from '@common/actions/table';
import { useMessage } from '@common/hooks/message';
import { useDatasheetNavigationContext, useDatasheetResultFieldEditable } from '../../context';

export type HyperlinkFieldProps = IDatasheetResultElement<DatasheetFieldComponentGetDto> & {
  context?: number;
  globalId?: number;
};

export const useHyperlinkFieldController = (props: HyperlinkFieldProps) => {
  const { datasheetElement, onChange, context, globalId } = props;
  const { showMessage, showConfirmMessage, showErrorMessage, closeMessage ,messageProps} = useMessage();

  const { registerField, focusNext, focusPrev } = useDatasheetNavigationContext();
  const elementId = datasheetElement.id;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const isEditable = useDatasheetResultFieldEditable(datasheetElement.original.editable !== false);

  React.useEffect(() => {
    if (!isEditable) return;
    registerField(elementId, { focus: () => inputRef.current?.focus() });
    return () => { registerField(elementId, null); };
  }, [elementId, registerField, isEditable]);

  const handleTabNavigation = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Tab') return;
      e.preventDefault();
      if (e.shiftKey) {
        focusPrev(elementId);
      } else {
        focusNext(elementId);
      }
    },
    [elementId, focusNext, focusPrev],
  );

  // Get field value - hyperlink fields use field.name (as per datasheet.ts comments)
  const getCurrentValue = () => {
    const field: any = datasheetElement.original.field;
    // Prefer name (what we write on submit), fall back to value for legacy data
    return field.name ?? field.value ?? '';
  };
  const [fieldCurrentValue, setFieldCurrentValue] = React.useState<string>(getCurrentValue());
  const [showUploadModal, setShowUploadModal] = React.useState(false);

  const { 
    headingSizeDependencies, 
    headingPlacedHorizontally, 
    headingPosition 
  } = getCommonFieldBasedElementParams(props);

  const baseDatasheetElementController = useDatasheetElementController(
    props,
    { headingSizeDependencies }
  );

  // Update value when datasheet element changes
  React.useEffect(() => {
    const newValue = getCurrentValue();
    if (fieldCurrentValue !== newValue) {
      setFieldCurrentValue(newValue);
    }
  }, [datasheetElement.original.field]);

  const handleFieldValueChange = React.useCallback((value: string) => {
    setFieldCurrentValue(value);
  }, []);

  const handleFieldValueSubmit = React.useCallback((value: string) => {
    onChange(datasheetElement.id, {
      ...datasheetElement,
      original: {
        ...datasheetElement.original,
        field: {
          ...datasheetElement.original.field,
          name: value,
          value,
        },
      },
    });
  }, [onChange, datasheetElement]);

  const handleFieldValueBlur = React.useCallback((value: string) => {
    // On blur, submit the value to trigger API call (same as onSubmit)
    handleFieldValueSubmit(value);
  }, [handleFieldValueSubmit]);

  const handleUploadClick = React.useCallback(() => {
    setShowUploadModal(true);
  }, []);

  const handleFileUpload = React.useCallback(async (file: File) => {
    // Blacklist dangerous extensions
    if (file && /\.(exe|bat)$/i.test(file.name)) {
      const msg = `File type not allowed: ${file.name}`;
      if (showErrorMessage) showErrorMessage(msg);
      setShowUploadModal(false);
      return;
    }

    // Get required data from props
    const field = datasheetElement.original.field;
    
    if (!globalId || !context || !field.fieldid) {
      const errorMessage = "Missing required data for upload. Please ensure all required fields are available.";
      if (showMessage) {
        showMessage({
          header: 'Upload Error',
          content: errorMessage,
          size: 'small'
        });
      }
      setShowUploadModal(false);
      return;
    }

    const fieldId = field.fieldid;
    const tableId = field.table ? field.fieldid : 0;
    const rrn = field.table ? 1 : -1; // Placeholder for row number
    const isImage = field.imageField || false;

    // Convert fieldcontext string to number if needed
    let fieldContext = context;
    if (field.fieldcontext) {
      const contextStr = String(field.fieldcontext);
      if (contextStr === 'IDS_CONTEXT' || contextStr === '1') {
        fieldContext = 1;
      } else if (contextStr === 'PDS_CONTEXT' || contextStr === '2') {
        fieldContext = 2;
      } else if (contextStr === '8' || contextStr === 'TABLE_CONTEXT') {
        fieldContext = 8;
      } else {
        const parsed = parseInt(contextStr, 10);
        if (!isNaN(parsed)) {
          fieldContext = parsed;
        }
      }
    }

    // Helper to perform the upload with overwrite flag
    const performUpload = async (overwrite: boolean) => {
      return await uploadDocument(
        file,
        fieldId,
        fieldContext,
        globalId,
        tableId,
        rrn,
        {
          overwrite,
          isImage,
          isProfilePhoto: false
        }
      );
    };

    try {
      await performUpload(false);
      // Update the field value with the filename on successful upload
      const fileName = file.name;
      handleFieldValueSubmit(fileName);
      
      if (showMessage) {
        showMessage({
          header: 'Upload Success',
          content: `File "${fileName}" uploaded successfully.`,
          size: 'small'
        });
      }
      setShowUploadModal(false);
    } catch (error: any) {
      const errCode = Number(error?.code || 0);
      // Handle conflict (file exists) with confirmation
      if (errCode === 409) {
        const msg = error?.message || `File "${file.name}" already exists. Overwrite?`;
        if (showConfirmMessage) {
          showConfirmMessage({
            header: 'Confirmation message',
            size: 'small',
            onCustomOk: async () => {
              try {
                await performUpload(true);
                const fileName = file.name;
                handleFieldValueSubmit(fileName);
                if (closeMessage) closeMessage();
                setShowUploadModal(false);
                if (showMessage) {
                  showMessage({
                    header: 'Upload Success',
                    content: `File "${fileName}" uploaded successfully.`,
                    size: 'small'
                  });
                }
              } catch (owErr: any) {
                if (showErrorMessage) {
                  showErrorMessage(owErr?.message || 'Overwrite failed');
                }
              }
            },
            confirmText: 'Overwrite',
            cancelText: 'Cancel',
            content: msg,
          });
        }
        return;
      }

      // Show error message to user
      const errorMessage = error instanceof Error ? error.message : "File upload failed. Please try again.";
      if (showErrorMessage) {
        showErrorMessage(errorMessage);
      } else {
        console.error("showMessage function is not available!");
      }
    } finally {
      // Close the upload modal
      setShowUploadModal(false);
    }
  }, [datasheetElement, showMessage, showConfirmMessage, showErrorMessage, closeMessage, handleFieldValueSubmit, context, globalId]);

  return {
    isEditable,
    headingPlacedHorizontally,
    headingPosition,
    fieldCurrentValue,
    handleFieldValueChange,
    handleFieldValueSubmit,
    handleFieldValueBlur,
    handleUploadClick,
    showUploadModal,
    setShowUploadModal,
    handleFileUpload,
    messageProps,
    closeMessage,
    inputRef,
    handleTabNavigation,
    ...baseDatasheetElementController,
  };
};
